import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Paper,
  Collapse,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Switch,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PrintIcon from '@mui/icons-material/Print';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import PeopleIcon from '@mui/icons-material/People';
import TableViewIcon from '@mui/icons-material/TableView';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import * as XLSX from 'xlsx';

import { useAuth } from '../contexts/AuthContext';
import { createApiClient } from '../services/apiClient';
import { fetchPatients } from '../services/patientService';
import { systemAPI } from '../services/api';
import { normalizePatient, formatFieldValue } from '../utils/patientNormalizer';
import { normalizeGreekSearch } from '../utils/greekUtils';
import { formatDate } from '../utils/dateUtils';
import BatchDiagnosisLookup from '../components/batch-lookup/BatchDiagnosisLookup';

const QueryPage = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeMainTab, setActiveMainTab] = useState(
    searchParams.get('tab') === 'batch' ? 1 : 0
  );
  
  // State
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const [epembaseisOptions, setEpembaseisOptions] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    searchTerm: '',
    gender: '',
    ageFrom: '',
    ageTo: '',
    dateFrom: '',
    dateTo: '',
    lastVisitFrom: '',
    lastVisitTo: '',
    diagnosis: [],
    address: '',
    allergies: '',
    email: '',
    amka: '',
    am: '',
    logicalMode: 'AND',
    diagnosisMode: 'OR',
  });

  const apiClient = useMemo(() => {
    if (!isAuthenticated) return null;
    const token = sessionStorage.getItem('token');
    return createApiClient(token);
  }, [isAuthenticated]);

  const loadAllPatients = useCallback(async () => {
    if (!apiClient) return;
    setLoading(true);
    try {
      const pageSize = 100;
      const firstResult = await fetchPatients(apiClient, 1, pageSize);
      let all = Array.isArray(firstResult.patients) 
        ? firstResult.patients.map((p, i) => normalizePatient(p, i)) 
        : [];
      
      const totalPages = firstResult.pagination?.totalPages || 1;
      
      for (let page = 2; page <= totalPages; page++) {
        const result = await fetchPatients(apiClient, page, pageSize);
        if (Array.isArray(result.patients)) {
          const normalized = result.patients.map((p, i) => normalizePatient(p, all.length + i));
          all = [...all, ...normalized];
        }
      }
      
      // Deduplicate
      const seen = new Set();
      const unique = all.filter(p => {
        const pid = p.id || p.__recordId;
        if (!pid || seen.has(pid)) return false;
        seen.add(pid);
        return true;
      });
      
      setPatients(unique);
    } catch (err) {
      console.error('Failed to load patients for query:', err);
      setError(t('Failed to load patients'));
    } finally {
      setLoading(false);
    }
  }, [apiClient, t]);

  useEffect(() => {
    loadAllPatients();
  }, [loadAllPatients]);

  useEffect(() => {
    systemAPI.getEpembaseis()
      .then(res => setEpembaseisOptions(res.data.map(e => e.epembasi)))
      .catch(err => console.error('Failed to load epembaseis:', err));
  }, []);

  useEffect(() => {
    const diagnosisParam = searchParams.get('diagnosis');
    if (diagnosisParam) {
      setFilters(prev => ({
        ...prev,
        diagnosis: [diagnosisParam]
      }));
    }
    const tabParam = searchParams.get('tab');
    if (tabParam === 'batch') {
      setActiveMainTab(1);
    } else if (tabParam === 'filter') {
      setActiveMainTab(0);
    }
  }, [searchParams]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      gender: '',
      ageFrom: '',
      ageTo: '',
      dateFrom: '',
      dateTo: '',
      lastVisitFrom: '',
      lastVisitTo: '',
      diagnosis: [],
      address: '',
      allergies: '',
      email: '',
      amka: '',
      am: '',
      logicalMode: 'AND',
      diagnosisMode: 'OR',
    });
  };

  const duplicateIds = useMemo(() => {
    if (!showOnlyDuplicates || patients.length === 0) return new Set();
    
    const amkaMap = new Map();
    const nameMap = new Map();
    const dups = new Set();
    
    const isPlaceholder = (val, minLen = 1) => {
      if (!val) return true;
      const s = String(val).trim();
      if (s.length < minLen) return true;
      const low = s.toLowerCase();
      return low === '' || low === '—' || low === 'null' || low === 'undefined' || low === 'none' || low === 'unknown' || low === '0' || low === '...' || low === 'test';
    };

    patients.forEach(p => {
      // AMKA check
      const amkaRaw = p.amka || p.__amka;
      if (!isPlaceholder(amkaRaw, 4)) { // AMKA should be at least 4 chars to be a useful duplicate key
        const amka = String(amkaRaw).trim();
        if (!amkaMap.has(amka)) amkaMap.set(amka, []);
        amkaMap.get(amka).push(p.id);
      }
      
      // Name + Age check
      const lName = p.lastName || p.last_name;
      const fName = p.firstName || p.first_name;
      const age = p.age || p.__age;
      
      if (!isPlaceholder(lName, 2) && !isPlaceholder(fName, 2) && !isPlaceholder(age, 1)) {
        const key = `${normalizeGreekSearch(lName)}|${normalizeGreekSearch(fName)}|${String(age).trim()}`;
        if (!nameMap.has(key)) nameMap.set(key, []);
        nameMap.get(key).push(p.id);
      }
    });
    
    amkaMap.forEach(ids => { if (ids.length > 1) ids.forEach(id => dups.add(id)); });
    nameMap.forEach(ids => { if (ids.length > 1) ids.forEach(id => dups.add(id)); });
    
    return dups;
  }, [patients, showOnlyDuplicates]);

  const filteredPatients = useMemo(() => {
    return patients.filter(p => {
      // If duplicate mode is on, first check if patient is a potential duplicate
      if (showOnlyDuplicates && !duplicateIds.has(p.id)) {
        return false;
      }

      const matchResults = [];

      // Search Term (General search)
      if (filters.searchTerm) {
        const term = normalizeGreekSearch(filters.searchTerm);
        const matches = [
          p.__displayName,
          p.__amka,
          p.__phone,
          p.__am,
          p.id,
          p.__diagnosis,
          p.diagnosis,
          p.__address,
          p.address,
          p.__notes,
          p.notes,
        ].some(val => val && normalizeGreekSearch(String(val)).includes(term));
        matchResults.push(matches);
      }

      // Gender
      if (filters.gender) {
        matchResults.push(p.gender === filters.gender || p.__gender === filters.gender);
      }

      // Age Range
      const age = parseInt(p.age || p.__age);
      if (filters.ageFrom) matchResults.push(age >= parseInt(filters.ageFrom));
      if (filters.ageTo) matchResults.push(age <= parseInt(filters.ageTo));

      // Date Range (Card Date / Registration)
      const cardDate = new Date(p.card_date || p.datekarta || p.created_at || p.__createdAt);
      if (filters.dateFrom) matchResults.push(cardDate >= new Date(filters.dateFrom));
      if (filters.dateTo) {
        const dTo = new Date(filters.dateTo);
        dTo.setHours(23, 59, 59, 999);
        matchResults.push(cardDate <= dTo);
      }

      // Last Visit Range
      const lastVisitDateStr = p.last_visit || p.lastVisit || p.__lastVisit;
      if (filters.lastVisitFrom || filters.lastVisitTo) {
        if (lastVisitDateStr && lastVisitDateStr !== '—') {
          const lastVisitDate = new Date(lastVisitDateStr);
          if (filters.lastVisitFrom) matchResults.push(lastVisitDate >= new Date(filters.lastVisitFrom));
          if (filters.lastVisitTo) {
            const lvTo = new Date(filters.lastVisitTo);
            lvTo.setHours(23, 59, 59, 999);
            matchResults.push(lastVisitDate <= lvTo);
          }
        } else {
          matchResults.push(false);
        }
      }

      // Diagnosis Logic
      if (filters.diagnosis && filters.diagnosis.length > 0) {
        const patientDiagnosis = normalizeGreekSearch(p.diagnosis || p.__diagnosis || '');
        const normalizedFilters = filters.diagnosis.map(d => normalizeGreekSearch(d));
        
        const diagnosisMatch = filters.diagnosisMode === 'AND'
          ? normalizedFilters.every(d => patientDiagnosis.includes(d))
          : normalizedFilters.some(d => patientDiagnosis.includes(d));
          
        matchResults.push(diagnosisMatch);
      }

      // Field specific searches
      if (filters.address) matchResults.push(normalizeGreekSearch(p.address || p.__address || '').includes(normalizeGreekSearch(filters.address)));
      if (filters.allergies) matchResults.push(normalizeGreekSearch(p.allergies || p.__allergies || '').includes(normalizeGreekSearch(filters.allergies)));
      if (filters.email) matchResults.push(normalizeGreekSearch(p.email || p.__email || '').includes(normalizeGreekSearch(filters.email)));
      if (filters.amka) matchResults.push(String(p.amka || p.__amka || '').toLowerCase().includes(filters.amka.toLowerCase()));
      if (filters.am) matchResults.push(String(p.am || p.__am || '').toLowerCase().includes(filters.am.toLowerCase()));

      if (matchResults.length === 0) return true;

      return filters.logicalMode === 'AND' 
        ? matchResults.every(res => res) 
        : matchResults.some(res => res);
    });
  }, [patients, filters, showOnlyDuplicates, duplicateIds]);

  const columns = [
    { 
      field: 'idPt', 
      headerName: t('ID'), 
      width: 80,
      renderCell: (params) => (
        <Link 
          to={`/patients/${params.row.id}`} 
          style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 700 }}
        >
          {params.value}
        </Link>
      )
    },
    { 
      field: 'lastName', 
      headerName: t('Last Name'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => (
        <Link 
          to={`/patients/${params.row.id}`} 
          style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 600 }}
        >
          {params.value}
        </Link>
      )
    },
    { 
      field: 'firstName', 
      headerName: t('First Name'), 
      flex: 1, 
      minWidth: 120,
      renderCell: (params) => (
        <Link 
          to={`/patients/${params.row.id}`} 
          style={{ textDecoration: 'none', color: '#1976d2', fontWeight: 600 }}
        >
          {params.value}
        </Link>
      )
    },
    { field: 'age', headerName: t('Age'), width: 60 },
    { field: 'gender', headerName: t('Gender'), width: 50 },
    { field: 'amka', headerName: t('AMKA'), width: 110 },
    { field: 'lastVisit', headerName: t('Last Visit'), width: 110 },
    { field: 'diagnosis', headerName: t('Diagnosis'), flex: 1.5, minWidth: 150 },
    { field: 'allergies', headerName: t('Allergies'), width: 120 },
    { field: 'address', headerName: t('Address'), width: 150 },
    { field: 'cardDate', headerName: t('Card Date'), width: 110 },
  ];

  const rows = useMemo(() => {
    return filteredPatients.map((p, i) => ({
      id: p.id || i,
      idPt: p.idPt || p.id || p.__recordId,
      lastName: p.lastName || p.last_name || '—',
      firstName: p.firstName || p.first_name || '—',
      age: p.age || p.__age || '—',
      gender: t(p.gender || p.__gender || 'Other'),
      amka: p.amka || p.__amka || '—',
      phone: p.phone || p.__phone || '—',
      diagnosis: p.diagnosis || p.__diagnosis || '—',
      allergies: p.allergies || p.__allergies || '—',
      address: p.address || p.__address || '—',
      lastVisit: formatDate(p.last_visit || p.lastVisit || p.__lastVisit || ''),
      cardDate: formatDate(p.card_date || p.datekarta || p.created_at || p.__createdAt),
    }));
  }, [filteredPatients, t]);

  const handleExportExcel = () => {
    const dataToExport = rows.map(r => ({
      [t('ID')]: r.idPt,
      [t('Last Name')]: r.lastName,
      [t('First Name')]: r.firstName,
      [t('Age')]: r.age,
      [t('Gender')]: r.gender,
      [t('AMKA')]: r.amka,
      [t('Diagnosis')]: r.diagnosis,
      [t('Allergies')]: r.allergies,
      [t('Address')]: r.address,
      [t('Last Visit')]: r.lastVisit,
      [t('Card Date')]: r.cardDate,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Patients Result');
    XLSX.writeFile(workbook, `MyEMR_Query_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <html>
        <head>
          <title>${t('Patient Query Results')}</title>
          <style>
            body { font-family: sans-serif; padding: 20px; }
            h1 { color: #1976d2; border-bottom: 2px solid #1976d2; padding-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 10px; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .meta { margin-bottom: 20px; color: #666; font-size: 12px; }
          </style>
        </head>
        <body>
          <h1>${t('Patient Query Results')}</h1>
          <div class="meta">
            ${t('Date')}: ${new Date().toLocaleDateString()} | ${t('Total Count')}: ${rows.length}
          </div>
          <table>
            <thead>
              <tr>
                <th>${t('ID')}</th>
                <th>${t('Name')}</th>
                <th>${t('Age')}</th>
                <th>${t('Gender')}</th>
                <th>${t('AMKA')}</th>
                <th>${t('Diagnosis')}</th>
                <th>${t('Last Visit')}</th>
                <th>${t('Card Date')}</th>
              </tr>
            </thead>
            <tbody>
              ${rows.map(r => `
                <tr>
                  <td>${r.idPt}</td>
                  <td>${r.lastName} ${r.firstName}</td>
                  <td>${r.age}</td>
                  <td>${r.gender}</td>
                  <td>${r.amka}</td>
                  <td>${r.diagnosis}</td>
                  <td>${r.lastVisit}</td>
                  <td>${r.cardDate}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <script>
            window.onload = () => {
              window.print();
              window.onafterprint = () => window.close();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  if (loading && patients.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, background: 'linear-gradient(45deg, #1976d2, #64b5f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {t('Patient Query & Lookup')}
          </Typography>
          {activeMainTab === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('Results Found')}: <strong>{filteredPatients.length}/{patients.length}</strong>
            </Typography>
          )}
        </Box>
        {activeMainTab === 0 && (
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportExcel}
              disabled={rows.length === 0}
            >
              {t('Export')}
            </Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              disabled={rows.length === 0}
            >
              {t('Print')}
            </Button>
          </Stack>
        )}
      </Stack>

      <Paper sx={{ mb: 3, borderRadius: 2.5, bgcolor: 'background.paper', overflow: 'hidden' }}>
        <Tabs
          value={activeMainTab}
          onChange={(e, val) => {
            setActiveMainTab(val);
            setSearchParams({ tab: val === 1 ? 'batch' : 'filter' });
          }}
          textColor="primary"
          indicatorColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<FilterListIcon />}
            iconPosition="start"
            label={t('Clinical Query & Filters')}
            sx={{ fontWeight: 700, textTransform: 'none', py: 1.5 }}
          />
          <Tab
            icon={<TableViewIcon />}
            iconPosition="start"
            label={t('Batch Diagnosis Lookup (Import XLSX/CSV)')}
            sx={{ fontWeight: 700, textTransform: 'none', py: 1.5 }}
          />
        </Tabs>
      </Paper>

      {activeMainTab === 1 ? (
        <BatchDiagnosisLookup />
      ) : (
        <>
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer', justifyContent: 'space-between' }} onClick={() => setFiltersOpen(!filtersOpen)}>
              <Stack direction="row" spacing={1} alignItems="center">
                <FilterListIcon color="primary" />
                <Typography variant="h6" fontWeight={700}>{t('Filter Patients')}</Typography>
              </Stack>
              <IconButton size="small">
                {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={filtersOpen}>
          <Divider />
          <Box sx={{ px: 3, py: 1.5, bgcolor: 'rgba(0,0,0,0.01)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
              {t('Filter Logic')}:
            </Typography>
            <ToggleButtonGroup
              size="small"
              value={filters.logicalMode}
              exclusive
              onChange={(e, next) => next && setFilters(prev => ({ ...prev, logicalMode: next }))}
              color="primary"
            >
              <ToggleButton value="AND" sx={{ px: 2, py: 0.5 }}>{t('Match ALL (AND)')}</ToggleButton>
              <ToggleButton value="OR" sx={{ px: 2, py: 0.5 }}>{t('Match ANY (OR)')}</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="searchTerm"
                  label={t('Search by name, AMKA, diagnosis...')}
                  value={filters.searchTerm}
                  onChange={handleFilterChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>{t('Gender')}</InputLabel>
                  <Select
                      name="gender"
                      value={filters.gender}
                      label={t('Gender')}
                      onChange={handleFilterChange}
                    >
                    <MenuItem value="">{t('None')}</MenuItem>
                    <MenuItem value="Male">{t('Male')}</MenuItem>
                    <MenuItem value="Female">{t('Female')}</MenuItem>
                    <MenuItem value="Other">{t('Other')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} md={1}>
                <TextField
                  fullWidth
                  name="ageFrom"
                  type="number"
                  label={t('Age From')}
                  value={filters.ageFrom}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={6} md={1}>
                <TextField
                  fullWidth
                  name="ageTo"
                  type="number"
                  label={t('Age To')}
                  value={filters.ageTo}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  name="dateFrom"
                  type="date"
                  label={t('Date From')}
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  name="dateTo"
                  type="date"
                  label={t('Date To')}
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Row 2: Advanced Search Fields */}
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  name="amka"
                  label={t('AMKA')}
                  value={filters.amka}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  name="am"
                  label={t('AM')}
                  value={filters.am}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="address"
                  label={t('Address')}
                  value={filters.address}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  name="lastVisitFrom"
                  type="date"
                  label={t('Last Visit From')}
                  value={filters.lastVisitFrom}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  name="lastVisitTo"
                  type="date"
                  label={t('Last Visit To')}
                  value={filters.lastVisitTo}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Row 3: More Fields */}
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    {t('Diagnosis Search')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch 
                        size="small" 
                        checked={filters.diagnosisMode === 'AND'} 
                        onChange={(e) => setFilters(prev => ({ ...prev, diagnosisMode: e.target.checked ? 'AND' : 'OR' }))}
                      />
                    }
                    label={<Typography variant="caption" fontWeight={600}>{filters.diagnosisMode === 'AND' ? t('Must match ALL') : t('Match ANY')}</Typography>}
                    sx={{ m: 0 }}
                  />
                </Box>
                <Autocomplete
                  multiple
                  freeSolo
                  options={epembaseisOptions}
                  value={filters.diagnosis}
                  onChange={(e, newValue) => setFilters(prev => ({ ...prev, diagnosis: newValue }))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('Diagnosis')}
                      placeholder={t('Select or type diagnosis')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start" sx={{ ml: 1 }}>
                              <InfoIcon fontSize="small" color="action" />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="allergies"
                  label={t('Allergies')}
                  value={filters.allergies}
                  onChange={handleFilterChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  name="email"
                  label={t('Email')}
                  value={filters.email}
                  onChange={handleFilterChange}
                />
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 2 }}>
                <Button
                  startIcon={<PeopleIcon />}
                  onClick={() => setShowOnlyDuplicates(!showOnlyDuplicates)}
                  color={showOnlyDuplicates ? "error" : "primary"}
                  variant={showOnlyDuplicates ? "contained" : "text"}
                >
                  {showOnlyDuplicates ? t('Show All Patients') : t('Find Duplicates')}
                </Button>
                <Button 
                  startIcon={<ClearAllIcon />} 
                  onClick={() => {
                    clearFilters();
                    setShowOnlyDuplicates(false);
                  }}
                  color="inherit"
                >
                  {t('Clear Filters')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {showOnlyDuplicates && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            {t('Duplicate Detection Active')} — {duplicateIds.size} {t('potential duplicates found')}
          </AlertTitle>
          {t('Showing patients with identical AMKA or identical Name and Age.')} 
          {t(' Use the table below to review and clean up old records.')}
        </Alert>
      )}

      <Paper sx={{ height: 600, width: '100%', borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          loading={loading}
          sx={{
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(0,0,0,0.02)',
              fontWeight: 800,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        />
      </Paper>
        </>
      )}
    </Box>
  );
};

export default QueryPage;
